/* eslint-disable @typescript-eslint/no-explicit-any */
import { isNil, isString, isUndefined } from 'lodash';

function lowerify(parseable: any) {
  if (typeof parseable === 'object' && !Array.isArray(parseable)) {
    return Object.keys(parseable).reduce((acc, next) => {
      try {
        const key = next.toLocaleLowerCase();
        let output = parseable[next];
        if (isNil(output)) {
          return acc;
        }

        if (isString(output) && (key === 'attribute:img:name' || key === 'attribute:img:id')) {
          output = output.replace(/generation=[0-9]*&/, '');
        }
        if (isString(acc[key])) {
          output = output.trim();
        }

        acc[key] = output;
        return acc;
      } catch (e) {
        const msg = (e as Error).message || 'An error occurred during record deserialization - lowerify';
        // TODO: This does not work, it causes the build to explode in some random npm internal code
        // frantic stackoverflow searching suggests this could be caused by issues resolving
        // https://stackoverflow.com/a/69033317
        // service.loggingService.warn(msg);
        console.warn(msg); // eslint-disable-line no-console

        return acc;
      }
    }, {} as any);
  }
  return parseable;
}

// New properties generated by pivot3
// These account for post lowerification
const PARENT_INDX = '_parentindx';

class JofTreeBuilder {
  private skippedFirst = false;

  private topLevel: any[];
  private parentLevel: any[] | null;
  private childLevel: any[];
  private seenAggIndx: number;

  constructor() {
    this.topLevel = this.childLevel = [];
    // Initially there is no higher level
    this.parentLevel = null;
    this.seenAggIndx = 0;
  }

  // TODO: Also build the flatDataArray at this time
  public insert(row: any) {
    // we are going to use some properties of the new pivot3 for this
    // skip the first payload data
    if (!this.skippedFirst) {
      this.skippedFirst = true;
      return;
    }

    // we get things that look like .data[n].children[m]
    // We determine the agg index
    const path: string = row['@path'];
    // This split will return a blank initial part since the paths all start with .
    const parts = path.split('.').slice(1);

    // Detect the boundaries between agg levels for tree building
    const aggIndx: number = parts.length - 1;

    if (aggIndx > this.seenAggIndx) {
      this.parentLevel = this.childLevel;
      this.childLevel = [];
      this.seenAggIndx = aggIndx;
    }

    this.childLevel.push(row);

    // Note that this will drop rows that aren't correctly linked
    if (aggIndx > 0) {
      const parentIndx: number = row[PARENT_INDX];
      if (!isUndefined(parentIndx) && this.parentLevel && parentIndx < this.parentLevel.length) {
        this.parentLevel[parentIndx].children.push(row);
      } else {
        console.warn('malformed response data, expected parentIndx in row', row); // eslint-disable-line no-console
      }
    }
  }

  public unwrap() {
    return this.topLevel;
  }
}

export async function streamParseJof(reader: ReadableStreamDefaultReader<string>): Promise<any> {
  const builder = new JofTreeBuilder();

  let buffer = '';
  /* Progressively read blocks from the stream. These blocks cannot be assumed
   * to be chunked by lines or anything else.
   */
  let block = await reader.read();
  while (!block.done) {
    // SAFETY: !block.done implies value
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const parts = block.value.split('\n');
    // Now, we incrementally process the lines
    parts[0] = buffer + parts[0];
    // We parse up the last split, because that line may be a partial record
    // We will process the last part following the loop once we know it is truly complete
    for (let i = 0; i < parts.length - 1; i++) {
      const line = parts[i];
      if (line.trim().length > 0) {
        builder.insert(lowerify(JSON.parse(line)));
      }
    }
    buffer = parts[parts.length - 1];

    block = await reader.read();
  }
  // Process the final chunk
  buffer = buffer.trim();
  if (buffer.length > 0) {
    builder.insert(lowerify(JSON.parse(buffer)));
  }
  // This is a separate line as its hard to breakpoint inside pivotworker
  const result = builder.unwrap();
  return result;
}
